import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  LayoutDashboard, FileText, Grid3X3,
  Settings, LogOut, Menu, X, Bell,
  Search, ChevronDown, ChevronUp, BarChart3,
  UserCheck, ShieldCheck, Tag, Database, Inbox,
  Newspaper, FolderTree, UserCog, Package, ExternalLink, Sparkles, Wand2, Wallet, Users, Flag, Library, Upload, BrainCircuit, Mail } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { isTokenExpired, scheduleAutoLogout } from '../../utils/auth';
import IvisoraLogo from '../../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [openGroups, setOpenGroups] = useState({
    content: true,
    blog: true,
    moderation: true,
    media: false,
    marketplace: true,
    users: false,
  });
  const [showNotifications, setShowNotifications] = useState(false);

  const [user] = useState(() => {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      try { return JSON.parse(userData); } catch { return null; }
    }
    return null;
  });

  const autoLogoutRef = useRef(null);

  // Cancel any pending auto-logout timer on unmount
  useEffect(() => () => { if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current); }, []);

  // Schedule auto-logout once on mount based on token expiry
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token || isTokenExpired(token)) return;
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    autoLogoutRef.current = scheduleAutoLogout(
      token,
      ['admin_token', 'admin_user'],
      () => {
        toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
        navigate('/admin/login');
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token || !user) {
      navigate('/admin/login', { state: { from: location } });
      return;
    }
    // ── Fast local expiry check (no network round-trip) ───────────────────────
    if (isTokenExpired(token)) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin/login', { state: { from: location } });
      return;
    }
    // Validate token against the backend — catches revoked / rotated tokens
    if (API_URL) {
      fetch(`${API_URL}/api/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        if (!res.ok) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          navigate('/admin/login', { state: { from: location } });
        }
      }).catch(() => {});
    }
  }, [navigate, location, user]);

  useEffect(() => {
    let isMounted = true;
    const loadStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) return;
        const response = await fetch(`${API_URL}/api/admin/content/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok && isMounted) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {}
    };
    if (user) loadStats();
    return () => { isMounted = false; };
  }, [user]);

  const handleLogout = () => {
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast({ title: 'Logged out', description: 'You have been logged out successfully' });
    navigate('/admin/login');
  };

  const toggleGroup = (group) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // A nav entry is active for its own path and its sub-paths, but never when a
  // longer entry in the same list matches better — /admin/templates/import must
  // light up Import Template alone, not Templates as well.
  const isActive = (path) => {
    const { pathname } = location;
    if (pathname !== path && !pathname.startsWith(path + '/')) return false;

    const allPaths = navGroups
      .flatMap((g) => g.items.map((i) => i.path))
      .concat(platformItems.map((i) => i.path));
    return !allPaths.some(
      (other) => other !== path
        && other.startsWith(path + '/')
        && (pathname === other || pathname.startsWith(other + '/')),
    );
  };

  const pendingCount = stats?.submissions_pending || 0;

  const navGroups = [
    {
      id: 'content',
      label: 'Content',
      items: [
        { name: 'Templates', icon: FileText, path: '/admin/templates', badge: stats?.templates?.published },
        { name: 'Template Library', icon: Library, path: '/admin/template-library' },
        { name: 'Import Template', icon: Upload, path: '/admin/templates/import' },
        // The route has existed since the generator shipped; it simply had no
        // way in from the sidebar, so the page was reachable only by typing
        // the URL. Published results are reviewed under Moderation →
        // Template Review (/admin/ai-templates).
        { name: 'AI Template Generator', icon: BrainCircuit, path: '/admin/ai-template-generator' },
        { name: 'Elements', icon: Grid3X3, path: '/admin/elements', badge: stats?.elements },
        { name: 'Categories', icon: FolderTree, path: '/admin/categories', badge: stats?.categories },
        { name: 'Template Categories', icon: Tag, path: '/admin/template-categories' },
        { name: 'Collections', icon: Package, path: '/admin/collections' },
      ],
    },
    {
      id: 'blog',
      label: 'Blog',
      items: [
        { name: 'Blog Posts', icon: Newspaper, path: '/admin/blog', badge: stats?.blog_posts?.total },
        { name: 'Blog Categories', icon: Tag, path: '/admin/blog-categories' },
      ],
    },
    {
      id: 'moderation',
      label: 'Moderation',
      items: [
        { name: 'Submissions', icon: Inbox, path: '/admin/submissions', badge: pendingCount || undefined, badgeWarning: true },
        { name: 'Template Review', icon: Sparkles, path: '/admin/ai-templates' },
        { name: 'Approvals', icon: ShieldCheck, path: '/admin/approvals' },
        { name: 'Contact messages', icon: Mail, path: '/admin/contact-messages' },
      ],
    },
    {
      id: 'media',
      label: 'Media',
      items: [
        { name: 'Assets', icon: Database, path: '/admin/assets', badge: stats?.assets },
      ],
    },
    {
      id: 'marketplace',
      label: 'Marketplace',
      items: [
        { name: 'Creators', icon: Users, path: '/admin/marketplace/creators' },
        { name: 'Reports', icon: Flag, path: '/admin/marketplace/reports' },
        { name: 'Payouts', icon: Wallet, path: '/admin/marketplace/payouts' },
      ],
    },
    {
      id: 'users',
      label: 'Users',
      items: [
        { name: 'Designers', icon: UserCheck, path: '/admin/designers' },
        { name: 'Admin Users', icon: UserCog, path: '/admin/users', badge: stats?.admins },
      ],
    },
  ];

  const platformItems = [
    { name: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
    { name: 'Text Repair', icon: Wand2, path: '/admin/text-repair' },
    { name: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f6fa] flex">
      {/* ── Sidebar ── */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#0f1117] text-white transition-all duration-300 z-50 flex flex-col
          ${sidebarOpen ? 'w-[260px]' : 'w-[72px]'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/[0.07] flex-shrink-0">
          <Link to="/admin/dashboard" className="flex items-center gap-3 min-w-0" aria-label="iVisora Admin — home">
            {sidebarOpen ? (
              <div className="min-w-0 flex flex-col gap-0.5">
                <IvisoraLogo variant="white" className="h-6 transition-opacity duration-200 hover:opacity-90" />
                <p className="text-[10px] text-white/35 tracking-widest uppercase">Admin Panel</p>
              </div>
            ) : (
              <div
                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-900/40"
                style={{ background: 'linear-gradient(135deg,#7B61FF,#C93CFF 55%,#FF4FD8)' }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none">
                  <circle cx="12" cy="6.2" r="1.7" fill="white" />
                  <rect x="10.3" y="9.6" width="3.4" height="9.4" rx="1.7" fill="white" />
                </svg>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 text-white/50 hover:text-white"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10">
          {/* Dashboard */}
          <Link
            to="/admin/dashboard"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-[13.5px] font-medium
              ${isActive('/admin/dashboard')
                ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/30'
                : 'text-white/50 hover:bg-white/[0.06] hover:text-white/90'}`}
          >
            <LayoutDashboard className="w-[18px] h-[18px] flex-shrink-0" />
            {sidebarOpen && <span>Dashboard</span>}
          </Link>

          {/* Grouped Sections */}
          {navGroups.map(group => (
            <div key={group.id} className="pt-3">
              {sidebarOpen ? (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 pb-1.5 text-[10.5px] font-semibold text-white/30 uppercase tracking-widest hover:text-white/50 transition-colors"
                >
                  <span>{group.label}</span>
                  {openGroups[group.id]
                    ? <ChevronUp className="w-3 h-3" />
                    : <ChevronDown className="w-3 h-3" />}
                </button>
              ) : (
                <div className="mx-3 h-px bg-white/[0.06] mb-2" />
              )}

              {(!sidebarOpen || openGroups[group.id]) && (
                <div className="space-y-0.5">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-[13.5px]
                          ${active
                            ? 'bg-violet-600 text-white shadow-md shadow-violet-900/20'
                            : 'text-white/50 hover:bg-white/[0.06] hover:text-white/90'}`}
                      >
                        <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                        {sidebarOpen && (
                          <>
                            <span className="flex-1 truncate">{item.name}</span>
                            {item.badge !== undefined && item.badge > 0 && (
                              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md leading-none
                                ${item.badgeWarning
                                  ? 'bg-amber-500 text-white'
                                  : active
                                    ? 'bg-white/20 text-white'
                                    : 'bg-white/10 text-white/60'}`}>
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Platform section */}
          <div className="pt-3">
            {sidebarOpen ? (
              <div className="px-3 pb-1.5 text-[10.5px] font-semibold text-white/30 uppercase tracking-widest">
                Platform
              </div>
            ) : (
              <div className="mx-3 h-px bg-white/[0.06] mb-2" />
            )}
            {platformItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-[13.5px]
                    ${active
                      ? 'bg-violet-600 text-white shadow-md shadow-violet-900/20'
                      : 'text-white/50 hover:bg-white/[0.06] hover:text-white/90'}`}
                >
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {sidebarOpen && <span>{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="border-t border-white/[0.07] p-3 flex-shrink-0">
          <div className={`flex items-center gap-3 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-md shadow-violet-900/30">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-medium text-white truncate">{user.name}</p>
                  <p className="text-[11px] text-white/40 capitalize">{user.role?.replace('_', ' ')}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'ml-[260px]' : 'ml-[72px]'}`}>
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200/80 flex items-center justify-between px-6 sticky top-0 z-40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search templates, elements, blog..."
                className="pl-9 w-[420px] bg-gray-50 border-gray-200 text-sm focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="relative hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="w-5 h-5" />
                {pendingCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                )}
              </Button>

              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-gray-100/80 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <p className="font-semibold text-gray-900 text-sm">Notifications</p>
                      {pendingCount > 0 && (
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">
                          {pendingCount} pending
                        </Badge>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                      {pendingCount > 0 ? (
                        <button
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                          onClick={() => { navigate('/admin/submissions'); setShowNotifications(false); }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Inbox className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {pendingCount} pending submission{pendingCount !== 1 ? 's' : ''}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Designer submissions awaiting your review
                              </p>
                            </div>
                          </div>
                        </button>
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No new notifications</p>
                        </div>
                      )}
                    </div>
                    <div className="px-4 py-2.5 border-t border-gray-100">
                      <button
                        className="text-xs text-violet-600 hover:text-violet-700 font-medium w-full text-center"
                        onClick={() => { navigate('/admin/submissions'); setShowNotifications(false); }}
                      >
                        View all submissions →
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Link to="/" target="_blank">
              <Button variant="outline" size="sm" className="text-sm text-gray-600 gap-1.5 border-gray-200 hover:bg-gray-50">
                View Site
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-8 py-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
